import {useEffect,useRef,useState} from 'react';

type MaskedCard={id:string;brand:string;last4:string;expMonth:number;expYear:number};
type StripeCardElement={mount(element:HTMLElement):void;destroy():void};
type StripeElements={create(type:'card'):StripeCardElement};
type StripeInstance={elements():StripeElements;
  confirmCardSetup(secret:string,options:{payment_method:{
    card:StripeCardElement;billing_details:{name:string}}}):Promise<{
      error?:{message?:string};
      setupIntent?:{status:string;payment_method?:string};
    }>};
declare global{interface Window{
  Stripe?:(publishableKey:string)=>StripeInstance;
}}
type Setup={publishableKey:string;clientSecret:string};
const okCardId=(id:string)=>/^pm_[A-Za-z0-9]{5,80}$/.test(id);
const endpoint=(id:string)=>
  '/api/v1/staff/customers/'+encodeURIComponent(id)+'/cards/test';

/** Source FloatHelm /LIVE Agents v112.html 3457-3478, 7347-7351:
 * sidebar Usable Cards, Terminal/Key, Add Card. The Key panel is a Stripe
 * Elements-hosted iframe: PAN, CVV, and expiration never enter app state.
 */
export function ProfileSavedCards({customerId,staffId,location}:{
  customerId?:string|null;staffId?:string|null;location:string;
}){
  const [personCustomerId,setPersonCustomerId]=useState(customerId??null);
  const [cards,setCards]=useState<MaskedCard[]>([]);
  const [status,setStatus]=useState('');
  const [error,setError]=useState('');
  const [open,setOpen]=useState(false);
  const [busy,setBusy]=useState(false);
  const [setup,setSetup]=useState<Setup|null>(null);
  const [elementReady,setElementReady]=useState(false);
  const [cardholder,setCardholder]=useState('');
  const container=useRef<HTMLDivElement|null>(null);
  const stripeCard=useRef<{stripe:StripeInstance;element:StripeCardElement}|null>(null);
  useEffect(()=>{setPersonCustomerId(customerId??null);
    setCards([]);setError('');setStatus('');setOpen(false);setSetup(null);
  },[customerId,staffId,location]);
  useEffect(()=>{
    if(!personCustomerId)return;
    const controller=new AbortController();
    void fetch(endpoint(personCustomerId)+'?'+
      new URLSearchParams({location}),{
        credentials:'same-origin',cache:'no-store',signal:controller.signal,
      }).then(async response=>{
      const body=await response.json() as {ok:boolean;data?:{cards:MaskedCard[]}};
      if(!response.ok||!body.ok||!Array.isArray(body.data?.cards))
        throw Error('Saved cards unavailable.');
      if(!controller.signal.aborted)setCards(body.data.cards);
    }).catch(()=>{
      if(!controller.signal.aborted)setError('Saved cards unavailable.');
    });
    return ()=>controller.abort();
  },[personCustomerId,location]);
  useEffect(()=>{
    if(!open||!setup||!container.current)return;
    if(!window.Stripe){setError('Stripe card entry is unavailable.');return;}
    const stripe=window.Stripe(setup.publishableKey);
    const element=stripe.elements().create('card');
    element.mount(container.current);
    stripeCard.current={stripe,element};setElementReady(true);
    return ()=>{element.destroy();stripeCard.current=null;setElementReady(false);};
  },[open,setup]);
  const refresh=async(id:string)=>{
    const response=await fetch(endpoint(id)+'?'+
      new URLSearchParams({location}),{
        credentials:'same-origin',cache:'no-store'});
    const body=await response.json() as {ok:boolean;data?:{cards:MaskedCard[]}};
    if(!response.ok||!body.ok||!Array.isArray(body.data?.cards))
      throw Error('Saved cards unavailable.');
    setCards(body.data.cards);return body.data.cards;
  };
  const start=async()=>{
    if(busy)return;setBusy(true);setError('');setStatus('');
    try{
      let id=personCustomerId;
      if(!id&&staffId){
        // Same person, one account: the existing Begin Sale endpoint creates
        // only an internal customer ledger upon this explicit Key click.
        const response=await fetch('/api/v1/staff/team/'+
          encodeURIComponent(staffId)+'/sale-customer',{
          method:'POST',credentials:'same-origin',cache:'no-store',
          headers:{'content-type':'application/json'},
          body:JSON.stringify({location}),
        });
        const body=await response.json() as {ok:boolean;data?:{customerId:string}};
        if(!response.ok||!body.ok||!body.data?.customerId)
          throw Error('The person cannot be linked to card payment.');
        id=body.data.customerId;setPersonCustomerId(id);
      }
      if(!id)throw Error('The person is not available for card payment.');
      const response=await fetch(endpoint(id).replace('/cards/test','/cards/test-setup'),{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location}),
      });
      const body=await response.json() as {ok:boolean;data?:Setup;
        error?:{code?:string}};
      if(!response.ok||!body.ok||!body.data?.clientSecret||
        !body.data.publishableKey?.startsWith('pk_test_')){
        const code=body.error?.code;
        throw Error(code==='STAFF_TEST_CARD_NOT_CONFIGURED'?
          'Stripe TEST card setup is not enabled in staging.':
          code==='AUTH_REQUIRED'?'Please sign in to add a card.':
          code==='ACCESS_DENIED'?'Card setup is not authorized for this person.':
          code==='STAFF_TEST_CARD_PROVIDER_UNAVAILABLE'?
            'The Stripe TEST provider could not start secure card entry.':
          code==='STAFF_TEST_CARD_CUSTOMER_NOT_IN_STRIPE_ACCOUNT'?
            'The saved TEST customer is not recognized by this staging Stripe account. No card was saved.':
            'Secure Stripe TEST card setup is unavailable'+(code?' ('+code+')':'')+'.');
      }
      setSetup(body.data);setOpen(true);
    }catch(e){setError(e instanceof Error?e.message:
      'Secure Stripe TEST card setup unavailable.');}
    finally{setBusy(false);}
  };
  const save=async()=>{
    if(!personCustomerId||!stripeCard.current||!setup||busy)return;
    setBusy(true);setError('');
    try{
      const {stripe,element}=stripeCard.current;
      const result=await stripe.confirmCardSetup(setup.clientSecret,{
        payment_method:{card:element,billing_details:{name:cardholder.trim()}},
      });
      if(result.error||result.setupIntent?.status!=='succeeded')
        throw Error(result.error?.message??'Stripe did not confirm this card.');
      const list=await refresh(personCustomerId);
      if(result.setupIntent.payment_method&&
        okCardId(result.setupIntent.payment_method)&&
        !list.some(x=>x.id===result.setupIntent!.payment_method))
        throw Error('Stripe setup succeeded but the saved card is not yet listed.');
      setStatus('Stripe TEST card saved.');setOpen(false);setSetup(null);
      setCardholder('');
    }catch(e){setError(e instanceof Error?e.message:'Card could not be saved.');}
    finally{setBusy(false);}
  };
  const remove=async(methodId:string)=>{
    if(!personCustomerId||busy||!okCardId(methodId))return;
    setBusy(true);setError('');setStatus('');
    try{
      const response=await fetch(endpoint(personCustomerId)+'/'+
        encodeURIComponent(methodId),{
        method:'DELETE',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location}),
      });
      if(!response.ok)throw Error('Card could not be removed.');
      await refresh(personCustomerId);
      setStatus('Stripe TEST card removed.');
    }catch(e){setError(e instanceof Error?e.message:'Card removal unavailable.');}
    finally{setBusy(false);}
  };
  return <section className="ecfs-record-side-box" aria-label="Usable Cards">
    <strong>Usable Cards</strong>
    {cards.length
      ?cards.map(card=><div key={card.id} className="ecfs-helm-side-link">
        <span>{card.brand} •••• {card.last4} exp {String(card.expMonth).padStart(2,'0')}/{String(card.expYear).slice(-2)}</span>
        <button type="button" disabled={busy}
          aria-label={'Remove card ending '+card.last4}
          onClick={()=>void remove(card.id)}>×</button>
      </div>)
      :<span className="ecfs-helm-empty-field">No usable cards.</span>}
    <strong>Add Card</strong>
    <div className="ecfs-card-mode-tabs">
      <button type="button" disabled title="Terminal connection not yet available">Terminal</button>
      <button type="button" disabled={busy||open}
        onClick={()=>void start()}>Key</button>
    </div>
    {open&&<div className="ecfs-profile-waiver-backdrop" role="presentation">
      <section className="ecfs-profile-waiver-popup" role="dialog"
        aria-modal="true" aria-label="Add Card">
        <h2>Add Card</h2>
        <p>Stripe TEST: save a card for this person's future purchases.</p>
        <label>Name on Card
          <input aria-label="Name on Card" value={cardholder}
            onChange={event=>setCardholder(event.target.value)}/></label>
        <label>Card Number, Expiration, CVV and ZIP
          <div ref={container} aria-label="Secure Stripe card fields"/></label>
        <div className="ecfs-gift-email-setup">
          <button type="button" disabled={busy||!elementReady}
            onClick={()=>void save()}>
            {busy?'Saving…':'Save Card'}</button>
          <button type="button" disabled={busy} onClick={()=>{
            setOpen(false);setSetup(null);setError('');
          }}>Cancel</button>
        </div>
      </section>
    </div>}
    {status&&<small role="status">{status}</small>}
    {error&&<small role="alert">{error}</small>}
  </section>;
}
